// src/pages/SavePassword.tsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Save, VpnKey, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { passwordService } from "../services/password.service";
import { groupService } from "../services/group.service";
import { PasswordGroup } from "../types/group.types";

const SavePassword: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const generatedPassword = location.state?.generatedPassword || "";

  const [siteName, setSiteName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(generatedPassword);
  const [groupId, setGroupId] = useState<string>(""); // Altere para string
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [groups, setGroups] = useState<PasswordGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadGroups();
    }
  }, [isAuthenticated]);

  const loadGroups = async () => {
    try {
      const groupsData = await groupService.getAll();
      setGroups(groupsData);
      if (groupsData.length > 0 && !groupId) {
        setGroupId(groupsData[0]._id);
      }
    } catch (error) {
      console.error("Erro ao carregar grupos:", error);
    }
  };

  const generateRandomPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=<>?";
    let newPassword = "";
    for (let i = 0; i < 16; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPassword);
  };

  const handleGroupChange = (groupId: string) => {
    setGroupId(groupId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupId) {
      alert("Selecione um grupo para salvar a senha");
      return;
    }

    setLoading(true);
    try {
      await passwordService.create({
        siteName,
        username,
        password,
        groupId: groupId, // Não é necessário o `as string`
        url,
        notes,
      });
      alert("Senha salva com sucesso!");
      navigate("/dashboard");
    } catch (error: any) {
      alert(error.response?.data?.message || "Erro ao salvar senha");
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
            Você precisa estar logado para salvar senhas.
          </Alert>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Fazer Login
          </Button>
        </Box>
      </Container>
    );
  }

  if (groups.length === 0) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
            Você precisa criar pelo menos um grupo antes de salvar senhas.
          </Alert>
          <Button variant="contained" onClick={() => navigate("/create-group")}>
            Criar Grupo
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Save sx={{ fontSize: 40, color: "secondary.main", mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Salvar Nova Senha
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="siteName"
                label="Site/Aplicação"
                name="siteName"
                autoComplete="url"
                autoFocus
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                helperText="Nome do site ou aplicação"
              />

              <TextField
                margin="normal"
                fullWidth
                id="username"
                label="Usuário/Email"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                helperText="Nome de usuário ou email para login"
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label="Senha"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      <IconButton aria-label="generate password" onClick={generateRandomPassword} edge="end" color="primary">
                        <VpnKey />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Clique no ícone de chave para gerar uma senha aleatória"
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="group-select-label">Grupo</InputLabel>
                <Select
                  labelId="group-select-label"
                  id="group-select"
                  value={groupId}
                  label="Grupo"
                  onChange={(e) => handleGroupChange(e.target.value as string)}
                >
                  {groups.map((group) => (
                    <MenuItem key={group._id} value={group._id}>
                      {group.name}
                      {group.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          - {group.description}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                margin="normal"
                fullWidth
                id="url"
                label="URL (opcional)"
                name="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                helperText="Link para o site ou aplicação"
              />

              <TextField
                margin="normal"
                fullWidth
                id="notes"
                label="Observações (opcional)"
                name="notes"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                helperText="Informações adicionais sobre esta senha"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              >
                {loading ? "Salvando..." : "Salvar Senha"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SavePassword;
